import React, { useState, useEffect } from "react";
import ActivePickupStep from "./ActivePickupStep";
import ActiveDropoffStep from "./ActiveDropoffStep";
import { Package, Truck, CircleCheck } from "lucide-react";

const ActiveRidePage = ({
  activeRide,
  GetRideDetails,
  mobile,
  onGetNewRides,
}) => {
  const [started, setStarted] = useState(true);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [isThisLastStep, setIsThisLastStep] = useState(false);
  const [rideCompleted, setRideCompleted] = useState(false);
  /* ---------------- LOAD STEPS ---------------- */
  useEffect(() => {
    if (activeRide?.steps?.length) {
      setSteps(activeRide.steps);
    } else {
      setSteps([]);
    }
  }, [activeRide]);

  /* ---------------- DETERMINE CURRENT STEP ---------------- */
  useEffect(() => {
    if (!steps.length) {
      setCurrentStep(null);
      return;
    }

    const nextStep = steps.find(
      (step) => step.status !== "COMPLETED"
    );
   
    setCurrentStep(nextStep || null);
    setIsThisLastStep(
      nextStep ? steps.indexOf(nextStep) === steps.length - 1 : false
    );
  }, [steps]);

  /* ---------------- STEP COMPLETION HANDLER ---------------- */
  const handleStepCompleted = async () => {
    if(isThisLastStep){
     setRideCompleted(true);
    }else{
    setRideCompleted(false);
    await GetRideDetails(mobile);
    }
    
  };

  /* ---------------- RIDE COMPLETION CHECK ---------------- */
  const isRideCompleted =
    steps.length > 0 &&
    steps.every((step) => step.status === "COMPLETED");

  /* ---------------- STICKY HEADER ---------------- */
  const RideHeader = () => (
    <div className="sticky top-0 z-20 bg-white border-b shadow-sm px-5 py-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-bold text-lg text-gray-900">
            Shipment {activeRide?.shipmentNumber || "-"}
          </h2>
          <p className="text-xs text-gray-500">
            {steps.filter(s => s.status === "COMPLETED").length} / {steps.length} steps completed
          </p>
        </div>

        {isRideCompleted && (
          <CircleCheck className="text-green-600 w-6 h-6" />
        )}
      </div>
    </div>
  );

  /* ---------------- START SCREEN ---------------- */
  if (!started && !isRideCompleted) {
    return (
      <div className="h-full flex flex-col">
        <RideHeader />

        <div className="p-5">
          <div className="bg-white rounded-2xl shadow p-6 text-center">
            <p className="text-gray-600 mb-6">
              {steps.length} steps to complete this shipment
            </p>

            <button
              onClick={() => setStarted(true)}
              className="w-full py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700"
            >
              Start Ride
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- RIDE COMPLETED UI ---------------- */
  if (rideCompleted) {
    return (
      <div className="h-full flex flex-col">
        <RideHeader />

        <div className="p-5">
          <div className="bg-white rounded-2xl shadow p-8 text-center">
            <CircleCheck className="mx-auto text-green-600 w-16 h-16 mb-4" />

            <h2 className="font-bold text-xl text-green-700 mb-2">
              Delivery Completed
            </h2>

            <p className="text-sm text-gray-600 mb-6">
              Shipment {activeRide?.shipmentNumber} has been delivered successfully.
            </p>

            <button
              onClick={onGetNewRides}
              className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
            >
              Get New Rides
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- ACTIVE STEP UI ---------------- */
  return (
    <div className="h-full flex flex-col">
      <RideHeader />

      <div className="p-5">
        {/* Step Header */}
        {currentStep && (
          <div className="flex items-center gap-3 mb-4">
            {currentStep.type === "PICKUP" && (
              <Package className="text-blue-600" />
            )}
            {currentStep.type === "DROPOFF" && (
              <Truck className="text-purple-600" />
            )}

            <div>
              <h3 className="font-bold text-gray-900">
                Step {currentStep.step} – {currentStep.type}
              </h3>
              <p className="text-xs text-gray-500">
                Reference: {currentStep.refNumber}
              </p>
            </div>
          </div>
        )}

        {/* Step Renderer */}
        {currentStep?.type === "PICKUP" && (
          <ActivePickupStep
            step={currentStep}
            onComplete={handleStepCompleted}
            activeRide={activeRide}
          />
        )}

        {currentStep?.type === "DROPOFF" && (
          <ActiveDropoffStep
            step={currentStep}
            onComplete={handleStepCompleted}
            activeRide={activeRide}
          />
        )}
      </div>
    </div>
  );
};

export default ActiveRidePage;
